import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import useOrganizationStore from '@/stores/SuperAdminStore/OrganizationStore';
import './OrganizationModal.css';

interface OrganizationModalProps {
  organization: {
    id?: number;
    name: string;
    isActive: boolean;
  };
  onClose: () => void;
  onSave: (data: { name: string; isActive: boolean }) => Promise<void>;
}

const OrganizationModal: React.FC<OrganizationModalProps> = ({
  organization,
  onClose,
  onSave,
}) => {
  const { creating, updating } = useOrganizationStore();
  const [formData, setFormData] = useState({
    name: organization.name,
    isActive: organization.isActive,
  });
  const [errors, setErrors] = useState<{ name?: string }>({});

  const isEditMode = !!organization.id;
  const isSaving = creating || updating;

  const validateForm = (): boolean => {
    const newErrors: { name?: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Organization name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Organization name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    await onSave({
      name: formData.name.trim(),
      isActive: formData.isActive,
    });
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSaving) {
      onClose();
    }
  };

  return (
    <div className="organization-modal-backdrop" onClick={handleBackdropClick}>
      <div className="organization-modal">
        <div className="organization-modal-header">
          <h2>{isEditMode ? 'Edit Organization' : 'Create Organization'}</h2>
          <button
            className="organization-modal-close"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="organization-modal-body">
            <div className="organization-modal-field">
              <label htmlFor="org-name">
                Organization Name <span className="required">*</span>
              </label>
              <input
                id="org-name"
                type="text"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (errors.name) setErrors({ ...errors, name: undefined });
                }}
                placeholder="Enter organization name"
                className={errors.name ? 'error' : ''}
                disabled={isSaving}
                autoFocus
              />
              {errors.name && (
                <span className="organization-modal-error">{errors.name}</span>
              )}
            </div>

            <div className="organization-modal-field">
              <label className="organization-modal-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  disabled={isSaving}
                />
                <span>Active</span>
              </label>
              <p className="organization-modal-help-text">
                {formData.isActive
                  ? 'This organization is active and can be assigned to users'
                  : 'This organization is inactive and cannot be assigned to new users'}
              </p>
            </div>
          </div>

          <div className="organization-modal-footer">
            <button
              type="button"
              className="organization-modal-btn cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="organization-modal-btn save"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrganizationModal;
